import * as R from 'ramda'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import { isEmptyOrNil } from 'libs/empty'

import { toMedia } from 'app/main/media/MediaMapper'

import { mediaType } from 'app/components/media/media'
import { parseMediaFromApi } from 'app/main/media/MediaMapper'
import { createEventAttributes } from 'app/main/notification/eventStartEndTime/EventStartEndTimeReducer'

import {
  SUBSCRIBERS_TYPE,
  GROUP_TYPE,
  ALL_GROUP_TYPE,
} from 'app/constants/recipient'
import {
  LINKEDIN_VALUE,
  TWITTER_VALUE,
  CHANNEL_APP,
  CHANNEL_SMS,
  CHANNEL_EMAIL,
  CHANNEL_CALL,
  FACEBOOK_VALUE,
  INSTAGRAM_VALUE,
  LINE_VALUE,
} from 'app/constants/channel'
import { MESSAGE_EXPIRATION_NONE } from 'app/main/notification/expiration/messageExpiration'
import {
  METERS_ID,
  FEET_ID,
  YARDS_ID,
  FEET_TEXT,
  YARDS_TEXT,
} from 'app/constants/beacon'
import { NOTI_PRIORITY_LOW, NOTI_PRIORITY_HIGH } from 'app/constants/priority'

import {
  GENERAL_DIALOG,
  TYPE_SEND_NOW,
  TYPE_SEND_SCHEDULE,
} from 'app/constants/compose'
import { TYPE_GENERAL, TYPE_POLL } from 'app/constants/messageType'

export const RECIPIENT_TYPE_EVERY_ONE = 'TYPE_EVERY_ONE'
export const RECIPIENT_TYPE_GROUPS = 'TYPE_GROUPS'

const hasValueById = (data, id) =>
  R.compose(R.not, R.isNil, R.find(R.propEq('id', id)))(data)

const mediaTypeFromApi = (apiMediaType) => {
  switch (apiMediaType) {
    case 'image':
      return mediaType.image
    case 'video_link':
      return mediaType.video
    case 'pdf':
      return mediaType.pdf
    default:
      return undefined
  }
}

const getTranslation = R.curry((languageId, translationList) => {
  return R.compose(
    R.defaultTo(R.head(translationList)),
    R.find(R.propEq('languageId', languageId)),
  )(translationList)
})

const sortMediaFromSortIndex = R.sortWith([R.ascend(R.prop('sortIndex'))])

const getFormat = (date, timezone) => {
  return !isEmptyOrNil(date)
    ? momentTimezone.tz(moment(date).format(), timezone).format()
    : null
}

export const priorityFromApi = (priority) => {
  switch (priority) {
    case 1:
      return NOTI_PRIORITY_LOW.label
    case 2:
      return NOTI_PRIORITY_HIGH.label
    default:
      return undefined
  }
}

const mapGeneral = {
  id: R.prop('id'),
  titleBodyDescription: (languageId) => (notification) => {
    const getText = (values) => {
      const isPoll = notification.type === TYPE_POLL
      return {
        title: isPoll ? '' : values.title,
        body: isPoll ? '' : values.body,
        question: isPoll ? values.title || '' : '',
        additionalContext: isPoll
          ? values.description || values.body || ''
          : '',
      }
    }

    return R.compose(
      getText,
      getTranslation(languageId),
      R.prop('translations'),
    )(notification)
  },
  mediaType: R.compose(mediaTypeFromApi, R.path(['medias', 0, 'type'])),
  type: R.propOr(TYPE_GENERAL, 'type'),
  priority: R.prop('level'),
  isDraft: R.prop('isDraft'),
  media: R.compose(
    R.ifElse(
      R.o(R.not, R.isEmpty),
      (media) => {
        const sortMedia = sortMediaFromSortIndex(media)
        return toMedia(
          parseMediaFromApi(
            R.o(mediaTypeFromApi, R.compose(R.prop('type'), R.head), sortMedia),
            sortMedia,
          ),
        )
      },
      R.always(undefined),
    ),
    R.prop('medias'),
  ),
  recipientsAndIndividual: (recipients, everyone) => (notification) => {
    const subscribers = R.map(
      (item) => {
        return {
          ...item,
          id: item.id,
          name: `${item.firstName} ${item.lastName}`,
          type: SUBSCRIBERS_TYPE,
          groupName: null,
          subscribersCount: null,
          tags: item.tags,
          parentId: null,
          groupBy: 'INDIVIDUAL',
        }
      },
      R.prop('subscribers', notification),
    )

    const subGroups = R.filter(
      (item) =>
        R.find(R.propEq('id', item.id), R.prop('subGroups', notification)),
      recipients,
    )

    const subGroupEveryone = R.find(R.propEq('id', R.prop('id', everyone)))(
      R.prop('subGroups', notification),
    )

    if (!R.isNil(subGroupEveryone)) {
      return [...recipients, ...subscribers]
    }

    if (!R.isEmpty(subGroups) && R.isNil(subscribers)) {
      return [...recipients, ...subscribers]
    }

    if (!R.isNil(subscribers) && R.isEmpty(subGroups)) {
      return subscribers
    }
    const selectedGroup = R.reduce(
      (acc, item) => {
        if (R.isNil(item)) {
          return acc
        }
        const optionItemLength = R.compose(
          R.length,
          R.filter(R.propEq('parentId', R.prop('parentId', item))),
        )(recipients)

        const subGroupsLength = R.compose(
          R.length,
          R.filter(R.propEq('parentId', R.prop('parentId', item))),
        )(subGroups)

        if (
          subGroupsLength === optionItemLength &&
          !hasValueById(acc, R.prop('parentId', item))
        ) {
          return [
            ...acc,
            R.find(R.propEq('id', R.prop('parentId', item)), recipients),
            item,
          ]
        }

        return [...acc, item]
      },
      [],
      subGroups,
    )

    const selectedAllGroup = R.reduce(
      (acc, item) => {
        const optionAllGroupLength = R.compose(
          R.length,
          R.filter(R.propEq('type', GROUP_TYPE)),
        )(recipients)

        const selectedAllGroupLength = R.compose(
          R.length,
          R.filter(R.propEq('type', GROUP_TYPE)),
        )(selectedGroup)

        if (
          optionAllGroupLength === selectedAllGroupLength &&
          hasValueById(acc, ALL_GROUP_TYPE)
        ) {
          return [
            ...acc,
            R.find(R.propEq('id', ALL_GROUP_TYPE), recipients),
            item,
          ]
        }
        return [...acc, item]
      },
      [],
      selectedGroup,
    )

    return R.filter(Boolean, [...selectedAllGroup, ...subscribers])
  },
  recipients: (notification) => {
    const subGroups = R.compose(
      R.map((subGroup) => subGroup.id),
      R.filter((item) => R.isNil(item.deletedAt)),
    )(notification.subGroups)

    const mapSubscribers = notification.subscribers.map((item) => {
      item.name = `${item.firstName} ${item.lastName}`
      item.type = 'subscribers'
      return item
    })

    return { subGroups, subscribers: mapSubscribers }
  },
  sendOptions: (notification) =>
    R.prop('scheduleDate', notification) ? TYPE_SEND_SCHEDULE : TYPE_SEND_NOW,
  scheduleDateTime: (notification) =>
    getFormat(
      R.prop('scheduleDate', notification),
      R.prop('scheduleTimezone', notification),
    ),
  scheduleTimezone: (notification) => R.prop('scheduleTimezone', notification),
  checkReminder: (notification) =>
    !R.isNil(R.prop('reminderDate', notification)),
  reminderDateTime: (notification) =>
    getFormat(
      R.prop('reminderDate', notification),
      R.prop('reminderTimezone', notification),
    ),
  reminderTimezone: (notification) => R.prop('reminderTimezone', notification),
  event: (notification) =>
    createEventAttributes({
      startDateTime: getFormat(
        R.prop('startTime', notification),
        R.prop('startTimezone', notification),
      ),
      endDateTime: getFormat(
        R.prop('endTime', notification),
        R.prop('endTimezone', notification),
      ),

      lat: notification.latitude,
      lng: notification.longitude,
      timeZoneId: notification.timezone,

      startTimezone: notification.startTimezone,
      endTimezone: notification.endTimezone,
    }),
  location: R.prop('address'),
  eventPlace: (notification) => ({
    address: R.prop('address', notification),
    latitude: R.prop('latitude', notification),
    longitude: R.prop('longitude', notification),
    timeZoneId: R.prop('timezone', notification),
  }),
  eventStatus: (notification) => notification.eventStatus,
  allowConversation: R.prop('allowConversation'),
  isTtsEnabled: (notification) => R.prop('isTtsEnabled', notification) === 1,
  isSharingEnabled: R.prop('isSharingEnabled'),
  sendOptionsBeacon: (notification) =>
    R.isNil(notification.beaconSchedule.start_date)
      ? TYPE_SEND_NOW
      : TYPE_SEND_SCHEDULE,
  sendType: (notification) =>
    R.isNil(notification.beaconSchedule.start_date)
      ? 'sendNow'
      : 'sendSchedule',
  timezone: (notification) => R.prop('timezone', notification),
  limitAmount: (notification) => R.prop('limitedAmount', notification),
  voucherUseCount: R.propOr(0, 'voucherUseCount'),
  isActive: R.prop('isActive'),
  voucher: R.prop('voucher'),
  linkedin: (notification) =>
    Boolean(R.find((item) => item === LINKEDIN_VALUE, notification.channels)),
  line: (notification) =>
    Boolean(R.find((item) => item === LINE_VALUE, notification.channels)),
  twitter: (notification) =>
    Boolean(R.find((item) => item === TWITTER_VALUE, notification.channels)),
  socialChannels: (notification) => {
    return {
      pages: notification.pageIds,
      channels: R.filter(
        (item) =>
          R.includes(item, [
            LINKEDIN_VALUE,
            TWITTER_VALUE,
            CHANNEL_APP,
            CHANNEL_SMS,
            CHANNEL_EMAIL,
            CHANNEL_CALL,
            FACEBOOK_VALUE,
            INSTAGRAM_VALUE,
            LINE_VALUE,
          ]),
        notification.channels,
      ),
    }
  },
  topics: (notification) => R.prop('placeTopics', notification),
  expirationTime: R.propOr(MESSAGE_EXPIRATION_NONE, 'expirationTime'),
  displayHistory: R.prop('displayHistory'),
  mediaSocials: (notification) =>
    R.map(
      (item) => {
        if (!item.id && item.mediaId) {
          item.id = item.mediaId
        }
        item.isSelected = item.isSelected == 1 || item.isSelected == true
        return item
      },
      R.prop('mediaSocials', notification),
    ),
  channels: (notification) => [
    ...R.map((item) => {
      return item.id
    }, notification.pageIds),
    ...notification.channels,
  ],
  pageIds: (notification) => R.map(R.prop('id'), notification.pageIds),
  isSent: (notification) => moment(notification.sendDate).isBefore(moment()),
  beacons: R.prop('beacons'),
  beaconSchedule: R.prop('beaconSchedule'),
  proximityLength: (notification) => {
    switch (notification.beaconSchedule.range_unit) {
      case FEET_TEXT:
        return FEET_ID
      case YARDS_TEXT:
        return YARDS_ID
      default:
        return METERS_ID
    }
  },
  range: (notification) => {
    return notification.beaconSchedule.range
  },
  addEndDateSchedule: (notification) =>
    R.compose(R.not, R.isNil)(notification.beaconSchedule.end_date),
  requestId: (notification) => R.prop('requestId', notification),
  getPoll: (notification) => {
    const poll = R.prop('poll', notification)
    if (R.isNil(poll)) {
      return poll
    }
    const pollEndDate = R.prop('endDate', poll)
    const pollEndTimezone = R.prop('endTimezone', poll)
    const endDateValue =
      pollEndDate && typeof pollEndDate === 'object' && pollEndDate.dateTime
        ? pollEndDate.dateTime
        : pollEndDate
    return {
      ...poll,
      endDate: getFormat(endDateValue, pollEndTimezone),
      endDateTimezone: pollEndTimezone,
    }
  },
}

const mapBeacon = {
  id: R.prop('id'),
  titleBodyDescription: (languageId) => {
    return R.compose(
      R.pickAll(['title', 'body']),
      getTranslation(languageId),
      R.prop('translations'),
    )
  },
  mediaType: R.compose(mediaTypeFromApi, R.path(['medias', 0, 'type'])),
  type: R.propOr(TYPE_GENERAL, 'type'),
  priority: R.prop('level'),
  media: R.compose(
    R.ifElse(
      R.o(R.not, R.isEmpty),
      (media) => {
        const sortMedia = sortMediaFromSortIndex(media)
        return toMedia(
          parseMediaFromApi(
            R.o(mediaTypeFromApi, R.compose(R.prop('type'), R.head), sortMedia),
            sortMedia,
          ),
        )
      },
      R.always(undefined),
    ),
    R.prop('medias'),
  ),
  recipientsAndIndividual: (recipients, everyone) => (notification) => {
    const subscribers = R.map(
      (item) => {
        return {
          ...item,
          id: item.id,
          name: `${item.firstName} ${item.lastName}`,
          type: SUBSCRIBERS_TYPE,
          groupName: null,
          subscribersCount: null,
          tags: item.tags,
          parentId: null,
        }
      },
      R.prop('subscribers', notification),
    )

    const subGroups = R.filter(
      (item) =>
        R.find(R.propEq('id', item.id), R.prop('subGroups', notification)),
      recipients,
    )

    const subGroupEveryone = R.find(R.propEq('id', R.prop('id', everyone)))(
      R.prop('subGroups', notification),
    )

    if (!R.isNil(subGroupEveryone)) {
      return [...recipients, ...subscribers]
    }

    if (!R.isEmpty(subGroups) && R.isNil(subscribers)) {
      return [...recipients, ...subscribers]
    }

    if (!R.isNil(subscribers) && R.isEmpty(subGroups)) {
      return subscribers
    }
    const selectedGroup = R.reduce(
      (acc, item) => {
        if (R.isNil(item)) {
          return acc
        }
        const optionItemLength = R.compose(
          R.length,
          R.filter(R.propEq('parentId', R.prop('parentId', item))),
        )(recipients)

        const subGroupsLength = R.compose(
          R.length,
          R.filter(R.propEq('parentId', R.prop('parentId', item))),
        )(subGroups)

        if (
          subGroupsLength === optionItemLength &&
          !hasValueById(acc, R.prop('parentId', item))
        ) {
          return [
            ...acc,
            R.find(R.propEq('id', R.prop('parentId', item)), recipients),
            item,
          ]
        }

        return [...acc, item]
      },
      [],
      subGroups,
    )

    const selectedAllGroup = R.reduce(
      (acc, item) => {
        const optionAllGroupLength = R.compose(
          R.length,
          R.filter(R.propEq('type', GROUP_TYPE)),
        )(recipients)

        const selectedAllGroupLength = R.compose(
          R.length,
          R.filter(R.propEq('type', GROUP_TYPE)),
        )(selectedGroup)

        if (
          optionAllGroupLength === selectedAllGroupLength &&
          hasValueById(acc, ALL_GROUP_TYPE)
        ) {
          return [
            ...acc,
            R.find(R.propEq('id', ALL_GROUP_TYPE), recipients),
            item,
          ]
        }
        return [...acc, item]
      },
      [],
      selectedGroup,
    )

    return R.filter(Boolean, [...selectedAllGroup, ...subscribers])
  },
  sendOptions: (notification) =>
    R.prop('scheduleDate', notification) ? TYPE_SEND_SCHEDULE : TYPE_SEND_NOW,
  scheduleDateTime: (notification) =>
    getFormat(
      R.prop('scheduleDate', notification),
      R.prop('scheduleTimezone', notification),
    ),
  scheduleTimezone: (notification) => R.prop('scheduleTimezone', notification),
  checkReminder: (notification) =>
    !R.isNil(R.prop('reminderDate', notification)),
  reminderDateTime: (notification) =>
    getFormat(
      R.prop('reminderDate', notification),
      R.prop('reminderTimezone', notification),
    ),
  reminderTimezone: (notification) => R.prop('reminderTimezone', notification),
  event: (notification) =>
    createEventAttributes({
      startDateTime: getFormat(
        R.prop('startTime', notification),
        R.prop('startTimezone', notification),
      ),
      endDateTime: getFormat(
        R.prop('endTime', notification),
        R.prop('endTimezone', notification),
      ),

      lat: notification.latitude,
      lng: notification.longitude,
      timeZoneId: notification.timezone,

      startTimezone: notification.startTimezone,
      endTimezone: notification.endTimezone,
    }),
  location: R.prop('address'),
  eventPlace: (notification) => ({
    address: R.prop('address', notification),
    latitude: R.prop('latitude', notification),
    longitude: R.prop('longitude', notification),
    timeZoneId: R.prop('timezone', notification),
  }),
  eventStatus: (notification) => notification.eventStatus,
  allowConversation: R.prop('allowConversation'),
  isTtsEnabled: (notification) => R.prop('isTtsEnabled', notification) === 1,
  isSharingEnabled: R.prop('isSharingEnabled'),
  sendOptionsBeacon: (notification) =>
    R.isNil(notification.beaconSchedule.start_date)
      ? TYPE_SEND_NOW
      : TYPE_SEND_SCHEDULE,
  sendType: (notification) =>
    R.isNil(notification.beaconSchedule.start_date)
      ? 'sendNow'
      : 'sendSchedule',
  timezone: (notification) => R.prop('timezone', notification),
  limitAmount: (notification) => R.prop('limitedAmount', notification),
  voucherUseCount: R.propOr(0, 'voucherUseCount'),
  isActive: R.prop('isActive'),
  voucher: R.prop('voucher'),
  linkedin: (notification) =>
    Boolean(R.find((item) => item === LINKEDIN_VALUE, notification.channels)),
  line: (notification) =>
    Boolean(R.find((item) => item === LINE_VALUE, notification.channels)),
  twitter: (notification) =>
    Boolean(R.find((item) => item === TWITTER_VALUE, notification.channels)),
  socialChannels: (notification) => {
    return {
      pages: notification.pageIds,
      channels: R.filter(
        (item) =>
          R.includes(item, [
            LINKEDIN_VALUE,
            LINE_VALUE,
            TWITTER_VALUE,
            CHANNEL_APP,
            CHANNEL_SMS,
            CHANNEL_EMAIL,
            CHANNEL_CALL,
            FACEBOOK_VALUE,
            INSTAGRAM_VALUE,
          ]),
        notification.channels,
      ),
    }
  },
  topics: (notification) => R.prop('placeTopics', notification),
  expirationTime: R.propOr(MESSAGE_EXPIRATION_NONE, 'expirationTime'),
  displayHistory: R.prop('displayHistory'),
  mediaSocials: (notification) =>
    R.map(
      (item) => {
        if (!item.id && item.mediaId) {
          item.id = item.mediaId
        }
        item.isSelected = item.isSelected == 1 || item.isSelected == true
        return item
      },
      R.prop('mediaSocials', notification),
    ),
  channels: (notification) => [
    ...R.map((item) => {
      return item.id
    }, notification.pageIds),
    ...notification.channels,
  ],
  pageIds: (notification) => R.map(R.prop('id'), notification.pageIds),
  isSent: (notification) => moment(notification.sendDate).isBefore(moment()),
  beacons: R.prop('beacons'),
  beaconSchedule: R.prop('beaconSchedule'),
  proximityLength: (notification) => {
    switch (notification.beaconSchedule.range_unit) {
      case FEET_TEXT:
        return FEET_ID
      case YARDS_TEXT:
        return YARDS_ID
      default:
        return METERS_ID
    }
  },
  range: (notification) => {
    return notification.beaconSchedule.range
  },
  addEndDateSchedule: (notification) =>
    R.compose(R.not, R.isNil)(notification.beaconSchedule.end_date),
  requestId: (notification) => R.prop('requestId', notification),
}

export const mapFromOtherPage = (notification, state) => {
  return {
    recipients: mapGeneral.recipientsAndIndividual(
      state.recipientForValue,
      state.everyone,
    )(notification),
  }
}

export const mapFromApi = (type, notification, defaultLanguage) => {
  return R.equals(type, GENERAL_DIALOG)
    ? {
        id: mapGeneral.id(notification),
        ...mapGeneral.titleBodyDescription(defaultLanguage.id)(notification),
        recipients: mapGeneral.recipients(notification),
        isDraft: mapGeneral.isDraft(notification),
        notiType: mapGeneral.type(notification),
        priority: mapGeneral.priority(notification),
        media: mapGeneral.media(notification),
        sendOptions: mapGeneral.sendOptions(notification),
        scheduleDateTime: mapGeneral.scheduleDateTime(notification),
        checkReminder: mapGeneral.checkReminder(notification),
        scheduleTimezone: mapGeneral.scheduleTimezone(notification),
        reminderDateTime: mapGeneral.reminderDateTime(notification),
        reminderTimezone: mapGeneral.reminderTimezone(notification),
        event: mapGeneral.event(notification),
        location: mapGeneral.location(notification),
        eventPlace: mapGeneral.eventPlace(notification),
        eventStatus: mapGeneral.eventStatus(notification),
        allowConversation: mapGeneral.allowConversation(notification),
        isTtsEnabled: mapGeneral.isTtsEnabled(notification),
        timezone: mapGeneral.timezone(notification),
        voucher: mapGeneral.voucher(notification),
        limitAmount: mapGeneral.limitAmount(notification),
        isSharingEnabled: mapGeneral.isSharingEnabled(notification),
        socialChannels: mapGeneral.socialChannels(notification),
        twitter: mapGeneral.twitter(notification),
        linkedin: mapGeneral.linkedin(notification),
        line: mapGeneral.line(notification),
        topics: mapGeneral.topics(notification),
        expirationTime: mapGeneral.expirationTime(notification),
        displayHistory: mapGeneral.displayHistory(notification),
        mediaSocials: mapGeneral.mediaSocials(notification),
        channels: mapGeneral.channels(notification),
        isSent: mapGeneral.isSent(notification),
        requestId: mapGeneral.requestId(notification),
        poll: mapGeneral.getPoll(notification),
      }
    : {
        id: mapBeacon.id(notification),
        ...mapBeacon.titleBodyDescription(defaultLanguage.id)(notification),
        recipients: mapGeneral.recipients(notification),
        notiType: mapBeacon.type(notification),
        sendOptions: mapBeacon.sendOptionsBeacon(notification),
        priority: mapBeacon.priority(notification),
        isTtsEnabled: mapBeacon.isTtsEnabled(notification),
        allowConversation: mapBeacon.allowConversation(notification),
        isSharingEnabled: mapBeacon.isSharingEnabled(notification),
        socialChannels: mapBeacon.socialChannels(notification),
        twitter: mapBeacon.twitter(notification),
        linkedin: mapBeacon.linkedin(notification),
        line: mapBeacon.line(notification),
        channels: mapBeacon.channels(notification),
        isSent: mapBeacon.isSent(notification),
        beacons: mapBeacon.beacons(notification),
        beaconSchedule: mapBeacon.beaconSchedule(notification),
        proximityLength: mapBeacon.proximityLength(notification),
        range: mapBeacon.range(notification),
        isActive: mapBeacon.isActive(notification),
        voucher: mapBeacon.voucher(notification),
        addEndDateSchedule: mapBeacon.addEndDateSchedule(notification),
        mediaSocials: mapBeacon.mediaSocials(notification),
      }
}
